/**
 * Bill of Materials sections — single source of truth.
 *
 * The BOM is emitted by four renderers (screen, print, PDF, CSV) and every one
 * of them needs the same list in the same order with the same running line
 * numbers. Before i18n each renderer carried its own copy, and they had already
 * drifted: the screen and the CSV said "Wall&CeilingBoard" while the PDF said
 * "Wall & Ceiling Board", and the line-number arithmetic was written out three
 * times. Translating three divergent copies would have tripled that problem, so
 * they collapse to this one.
 *
 * `titleKey` rather than a title: the section heading is a product family name
 * that the renderer resolves through i18n at draw time.
 *
 * `startLineNo` is precomputed here because it is a property of the section
 * *list*, not of any one renderer. Line numbers run continuously down the whole
 * BOM (1..N, never resetting at a section break) so a contractor can say "line
 * item 14" and mean the same row on the page, in the PDF and in the export.
 * Note it counts every section, including empty ones — which contribute zero —
 * so filtering empties out afterwards does not disturb the numbering.
 */

import { BillOfMaterials, EstimateOutput, MaterialItem, SurfaceBreakdown } from '../types';
import { tr } from '../i18n/standalone';

/** Which lucide icon family a section's heading gets on screen. */
export type BomSectionIcon = 'panel' | 'slatwall' | 'trim';

export interface BomSection {
  /** Stable identity — safe to switch on, unlike the translated heading. */
  key: string;
  /** i18n key for the section heading (namespace-qualified). */
  titleKey: string;
  items: MaterialItem[];
  /** Panels carry a Total Sqft column; trims do not. */
  isPanel: boolean;
  icon: BomSectionIcon;
  /** 1-based line number of this section's first row across the whole BOM. */
  startLineNo: number;
}

/**
 * The BOM sections in output order.
 *
 * Includes empty sections — callers skip them (`items.length === 0`) after the
 * line numbers are assigned. The legacy 1/2" and 3/4" J-Trims stay in the list
 * for v1 estimates loaded from storage; new estimates leave them empty and
 * populate Snap-In Trim J instead.
 */
export function bomSections(bom: BillOfMaterials): BomSection[] {
  const defs: Array<Omit<BomSection, 'startLineNo'>> = [
    { key: 'wallAndCeilingBoard', titleKey: 'results:bom.sections.wallAndCeilingBoard', items: bom.wallAndCeilingBoard, isPanel: true, icon: 'panel' },
    { key: 'slatwall', titleKey: 'results:bom.sections.slatwall', items: bom.slatwall, isPanel: true, icon: 'slatwall' },
    { key: 'hTrims', titleKey: 'results:bom.sections.hTrims', items: bom.hTrims, isPanel: false, icon: 'trim' },
    { key: 'jTrimsSnapIn', titleKey: 'results:bom.sections.jTrimsSnapIn', items: bom.jTrimsSnapIn ?? [], isPanel: false, icon: 'trim' },
    { key: 'jTrimsHalfInch', titleKey: 'results:bom.sections.jTrimsHalfInch', items: bom.jTrimsHalfInch, isPanel: false, icon: 'trim' },
    { key: 'jTrimsThreeQuarterInch', titleKey: 'results:bom.sections.jTrimsThreeQuarterInch', items: bom.jTrimsThreeQuarterInch, isPanel: false, icon: 'trim' },
  ];

  let lineNo = 1;
  return defs.map((def) => {
    const section = { ...def, startLineNo: lineNo };
    lineNo += def.items.length;
    return section;
  });
}

/**
 * Display name for a surface in the active language.
 *
 * Generated names (the four full-room walls, the ceiling, unnamed zones) carry
 * a `surfaceNameKey` and translate. Names the user typed — an individual wall,
 * a named ceiling zone — have no key and are returned untouched, which is the
 * whole point of the split: nobody wants their "Back of shop" wall renamed by a
 * language switch. Estimates saved before the key existed also land here and
 * render their stored English name.
 */
export function surfaceLabel(surface: SurfaceBreakdown): string {
  return surface.surfaceNameKey
    ? tr(surface.surfaceNameKey, surface.surfaceNameParams)
    : surface.surfaceName;
}

/**
 * Whether the "System Notes" section renders anywhere (results page, print,
 * PDF). Switched off 2026-11-04: the notes read as calculation chatter (zone
 * boundary trim lines, mode labels) rather than anything a customer needs.
 * The notes are still computed and saved with the estimate; flip this back on
 * once there are content guidelines for what belongs there.
 */
export const SYSTEM_NOTES_ENABLED = false;

/**
 * The estimate's system notes in the active language.
 *
 * Freshly computed estimates carry `notesI18n` (key + params + English text);
 * ones saved before i18n carry only the English `notes` array, and render as
 * they always did. Used by the results page and the PDF, which is why it lives
 * here rather than in either of them. Returns nothing while
 * SYSTEM_NOTES_ENABLED is off, which hides the section in every renderer.
 */
export function resolveNotes(estimate: EstimateOutput): string[] {
  if (!SYSTEM_NOTES_ENABLED) return [];
  if (estimate.notesI18n?.length) {
    return estimate.notesI18n.map((n) => tr(n.key, n.params));
  }
  return estimate.notes ?? [];
}
